// useFeatureFlags.js
// File: nexusflow-saas/dashboard/src/hooks/useFeatureFlags.js

import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to check feature flags for current tenant
 * Returns object with flag checking methods
 */
export function useFeatureFlags() {
  const { currentUser } = useAuth();

  // Get features from current user (loaded from tenant in AuthContext)
  const features = currentUser?.features || {};

  /**
   * Check if a feature is enabled
   * @param {string} featureName - Name of the feature flag
   * @returns {boolean} - True if feature is enabled
   */
  const isFeatureEnabled = (featureName) => {
    // If no features loaded, default to enabled for backwards compatibility
    if (!features || Object.keys(features).length === 0) {
      return true;
    }

    return features[featureName] === true;
  };

  /**
   * Check if a decision mode is allowed
   * @param {string} mode - conservative, balanced, or aggressive
   * @returns {boolean} - True if mode is allowed
   */
  const isModeAllowed = (mode) => {
    if (!features.decision_mode_enabled) {
      // If mode selection disabled, only 'balanced' is allowed
      return mode === 'balanced';
    }

    const allowedModes = features.allowed_modes || ['conservative', 'balanced', 'aggressive'];
    return allowedModes.includes(mode);
  };

  /**
   * Get list of allowed decision modes
   * @returns {Array} - Array of allowed mode names
   */
  const getAllowedModes = () => {
    if (!features.decision_mode_enabled) {
      return ['balanced']; // Only balanced if mode selection disabled
    }

    return features.allowed_modes || ['conservative', 'balanced', 'aggressive'];
  };

  /**
   * Check if a language is enabled
   * @param {string} lang - Language code (en, ar)
   * @returns {boolean} - True if language is enabled
   */
  const isLanguageEnabled = (lang) => {
    const enabledLanguages = features.languages_enabled || ['en'];
    return enabledLanguages.includes(lang);
  };

  /**
   * Get list of enabled languages
   * @returns {Array} - Array of enabled language codes
   */
  const getEnabledLanguages = () => {
    return features.languages_enabled || ['en'];
  };

  /**
   * Check if user count is within limit
   * @param {number} currentCount - Current number of users
   * @returns {boolean} - True if within limit
   */
  const canAddUser = (currentCount) => {
    const maxUsers = features.max_users || 5;
    return currentCount < maxUsers;
  };

  /**
   * Get maximum allowed users
   * @returns {number} - Maximum number of users
   */
  const getMaxUsers = () => {
    return features.max_users || 5;
  };

  return {
    // Feature checks
    isFeatureEnabled,
    
    // Specific feature shortcuts
    canUseCampaigns: () => isFeatureEnabled('campaign_decision_enabled'),
canViewProducts: () => features.products_page_enabled !== false,
    canShare: () => isFeatureEnabled('share_enabled'),
    canDownloadPDF: () => isFeatureEnabled('pdf_enabled'),
    canViewAnalytics: () => isFeatureEnabled('analytics_enabled'),
    canUseAPI: () => isFeatureEnabled('api_access_enabled'),
    canExport: () => isFeatureEnabled('export_enabled'),
    hasCustomBranding: () => isFeatureEnabled('custom_branding_enabled'),
    
    // Decision modes
    isModeAllowed,
    getAllowedModes,
    canSelectMode: () => features.decision_mode_enabled !== false,
    
    // Languages
    isLanguageEnabled,
    getEnabledLanguages,
    
    // Users
    canAddUser,
    getMaxUsers,
    
    // Raw features object (for advanced use)
    features
  };
}